import { Component, Injector, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ReportTableComponent } from '@app/components';
import { AnalyticEvents } from '@app/enums';
import { AnalyticService, FileHelperService, NotifyService } from '@app/services';
import { Translatable } from '@app/types/translatable.type';
import { Survey } from '@models/performance/survey.model';
import { SurveyMenuService, SurveyService } from '@performance/services';
import { ReportFilter } from '@reporting/interfaces/report-filter.interface';
import { Report } from '@reporting/services/queries/report';
import { getSurveyResultsTitleTooltip } from '../../meta/tooltip-meta';

@Component({
    templateUrl: './raw-data-survey.template.html',
    providers: [
        {
            provide: Report,
            useFactory: (fileService: FileHelperService) => new Report(fileService),
            deps: [FileHelperService],
        },
    ],
})
export class RawDataSurveyComponent implements OnInit {
    @ViewChild('reportTable') reportTable: ReportTableComponent;

    survey: Survey = this.route.snapshot.data.models.get('survey');
    report: Report | null = null;
    isLoading = true;
    reportLoaded = false;
    filters: ReportFilter[] = [];
    routeToRedirectTo = '/surveys/center';
    surveyResultsTitleTooltip: Translatable = '';

    constructor(
        public tabMenuService: SurveyMenuService,
        private route: ActivatedRoute,
        private analyticsService: AnalyticService,
        private readonly notifyService: NotifyService,
        private readonly injector: Injector,
        private surveyService: SurveyService
    ) {}

    async ngOnInit(): Promise<void> {
        await this.loadReport();
        this.setTooltip();

        this.routeToRedirectTo = this.routeToRedirectTo.replace(':id', this.survey.id.toString());
    }

    async onExport(contentType: string): Promise<void> {
        if (!this.report) {
            return;
        }

        switch (contentType) {
            case 'csv':
                this.analyticsService.trackEvent(AnalyticEvents.IndividualSurveyClickViewResultsRawDataExportAsCSV);
                break;
            case 'xlxs':
                this.analyticsService.trackEvent(
                    AnalyticEvents.SurveysIndividualSurveyClickViewResultsRawDataExportAsExcel
                );
                break;
        }

        try {
            this.report.export(contentType);
        } catch (error) {
            this.notifyService.error('Error exporting report');
        }
    }

    private async loadReport(): Promise<void> {
        try {
            this.report = await this.injector
                .get(Report)
                .name('surveyReport')
                .filters([
                    {
                        name: 'surveyId',
                        value: this.survey.id,
                    },
                ])
                .page(1)
                .get();
            this.reportLoaded = this.report?.data?.sheets?.length > 0;
        } catch (error) {
            this.notifyService.error(error);
        } finally {
            this.isLoading = false;
        }
    }

    /**
     * Also retrieving the aggregatedData here to be able to easily get the correct data for the tooltip
     * We can't use the survey submission count because it is wrong.
     * The reason is because the answers are being encrypted (and stored like that at the DB level) even if the value is null.
     * So the query that aggregates can't exclude those values from the count.
     * Once that is fixed, we can use the original survey attributes instead of doing this way. (Maybe Survey V2)
     *
     * Note: I, Tom, moved the above comment. I can't ensure it's still correct, but seeing as survey v2 is not happening... I assume it's right.
     */
    private async setTooltip(): Promise<void> {
        try {
            const aggregatedData = await this.surveyService.getSurvey(this.survey.id);

            // aggregatedData[0] can be empty if user assigned a template with blank questions.
            this.surveyResultsTitleTooltip = getSurveyResultsTitleTooltip(
                aggregatedData[0]?.totalAnswers ?? 0,
                aggregatedData[0]?.totalParticipants ?? 0
            );
        } catch (err) {
            this.notifyService.error(err);
        }
    }
}
